/**
 * Seletor de Troncos para Chamadas de Entrada
 * VigosCloud - Incoming Call System
 * 
 * Exibe apenas troncos do tenant logado
 */

import React, { useEffect, useState } from 'react';
import { getAvailableIncomingTrunks } from '../../../../services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faServer, faSync, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

function TrunkSelector({ 
  selectedTrunk, 
  onTrunkChange, 
  tenant, 
  error, 
  disabled = false,
  required = false 
}) {
  const [availableTrunks, setAvailableTrunks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Carregar troncos disponíveis quando o tenant mudar
  useEffect(() => {
    if (tenant) {
      loadAvailableTrunks();
    } else {
      setAvailableTrunks([]);
    }
  }, [tenant]);

  const loadAvailableTrunks = async () => {
    if (!tenant) return;
    
    try {
      setLoading(true);
      setLoadError(null);
      
      console.log(`🔍 [TRUNK-SELECTOR] Carregando troncos para tenant: ${tenant}`);
      
      const response = await getAvailableIncomingTrunks(tenant);
      
      if (response?.success && response.data) {
        const filteredTrunks = response.data.filter(trunk => 
          trunk.accountcode === tenant || trunk.tenant === tenant
        );
        
        setAvailableTrunks(filteredTrunks);
        console.log(`✅ [TRUNK-SELECTOR] Carregados ${filteredTrunks.length} troncos do tenant`);
        
        if (filteredTrunks.length === 0 && selectedTrunk) {
          onTrunkChange('');
        }
        
        if (selectedTrunk && !filteredTrunks.find(trunk => trunk.name === selectedTrunk)) {
          console.log('⚠️ [TRUNK-SELECTOR] Tronco selecionado não está mais disponível, limpando seleção');
          onTrunkChange('');
        }
      } else {
        console.error('❌ [TRUNK-SELECTOR] Erro na resposta:', response);
        setLoadError(response?.error || 'Erro ao carregar troncos');
        setAvailableTrunks([]);
      }
    } catch (error) {
      console.error('❌ [TRUNK-SELECTOR] Erro ao carregar troncos:', error);
      setLoadError('Erro ao carregar troncos disponíveis');
      setAvailableTrunks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTrunkChange = (e) => {
    const value = e.target.value;
    console.log(`🔍 [TRUNK-SELECTOR] Tronco selecionado: ${value || 'Nenhum'}`);
    onTrunkChange(value);
  };

  const handleRefresh = () => {
    console.log('🔄 [TRUNK-SELECTOR] Atualizando lista de troncos');
    loadAvailableTrunks();
  };

  return (
    <div className="field">
      <label className="label">
        <FontAwesomeIcon icon={faServer} className="mr-2" />
        Tronco {required && <span className="has-text-danger">*</span>}
        {!required && <span className="has-text-grey">(Opcional)</span>}
      </label>
      
      <div className="field has-addons">
        <div className="control is-expanded">
          <div className="select is-fullwidth">
            <select
              value={selectedTrunk || ''}
              onChange={handleTrunkChange}
              disabled={disabled || loading}
              className={error ? 'is-danger' : ''}
              required={required}
            >
                      <option value="">
                        {loading ? 'Carregando troncos...' : 'Selecione um tronco'}
                      </option>
              {availableTrunks.map((trunk) => (
                <option key={trunk.name} value={trunk.name}>
                  {trunk.name} ({trunk.accountcode})
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="control">
          <button
            type="button"
            className={`button ${loading ? 'is-loading' : ''}`}
            onClick={handleRefresh}
            disabled={disabled || loading || !tenant}
            title="Atualizar lista de troncos"
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
        </div>
      </div>
      
      {/* Mensagens de erro */}
      {error && (
        <p className="help is-danger">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
          {error}
        </p>
      )}
      
      {loadError && (
        <p className="help is-danger">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
          {loadError}
        </p>
      )}
      
      {/* Informações sobre critérios de seleção */}
      <div className="help">
        <strong>Critério de seleção:</strong> Será exibido apenas o tronco do cliente logado.
        
        {!tenant && (
          <div className="has-text-warning mt-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
            Defina o cliente primeiro para carregar os troncos disponíveis.
          </div>
        )}
        
        {tenant && availableTrunks.length === 0 && !loading && !loadError && (
          <div className="has-text-warning mt-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
            Nenhum tronco disponível encontrado para o cliente {tenant}.
            <br />
            Verifique se existe um tronco configurado para este cliente.
          </div>
        )}
        
        {availableTrunks.length > 0 && (
          <div className="has-text-success mt-2">
            {/* ✅ {availableTrunks.length} tronco(s) disponível(is) para o cliente {tenant}. */}
          </div>
        )}
      </div>
    </div>
  );
}

export default TrunkSelector;
